<template>
    <a
        ref="button"
        :href="url"
        :target="determineTarget()"
        :title="title"
        class="relative cursor-pointer"
        @click="goToUrl">
        <slot />
    </a>
</template>

<script>
import { get, isUndefined, startsWith } from 'lodash';
import Vue from 'vue';

export default {
  name: 'TisButton',
  props: ['url', 'target', 'title', 'fullRedirect', 'analyticsData'],
  methods: {
    urlIsZiggyObject() {
      return typeof this.url === 'object' && get(this.url, 'ziggy');
    },

    urlContainsTisDomain() {
      const tisDomains = [
        'localhost',
        'tis.test',
        'client.tis.test',
        'theimagesalon.com',
        'imagesalon.com',
        'imagesalon.xyz',
        'client.imagesalon.com',
        'client.imagesalon.xyz',
        'imagesalon.co',
        'imgsln.com',
        'imgsln.co',
        'imgsln.test',
      ];

      return (new RegExp(tisDomains.join('|'))).test(this.url);
    },

    isNotInertiaCompatible() {
      return this.url === this.route('web.registration') || this.url === this.route('web.login') || this.url === this.route('web.blog');
    },

    isInfoEmail() {
      return this.url === 'mailto:info@theimagesalon.com' || this.url === 'mailto:info@imagesalon.com';
    },

    isPhotoEmail() {
      return this.url === 'mailto:photo@theimagesalon.com' || this.url === 'mailto:photo@imagesalon.com';
    },

    isPhone() {
      return startsWith(this.url, 'tel:');
    },

    isFacebookShare() {
      return startsWith(this.url, 'https://www.facebook.com/sharer/sharer.php');
    },

    isTwitterShare() {
      return startsWith(this.url, 'https://twitter.com/intent/tweet');
    },

    isLinkedInShare() {
      return startsWith(this.url, 'https://www.linkedin.com/shareArticle');
    },

    isFacebookGroup() {
      return this.url === 'https://www.facebook.com/groups/theimagesalon/';
    },

    isInstagram() {
      return this.url === 'https://www.instagram.com/imgsalon/';
    },

    isExternal() {
      if (this.isPhone()) {
        return false;
      }

      if (this.isFacebookGroup()) {
        return true;
      }

      if (this.isInstagram()) {
        return true;
      }

      if (get(this, 'target') === '_blank') {
        return true;
      }

      if (startsWith(this.url, 'mailto:')) {
        return true;
      }

      if (startsWith(this.url, '/')) {
        return false;
      }

      // check if this.url is a ziggy object
      if (this.urlIsZiggyObject()) {
        return false;
      }

      return !this.urlContainsTisDomain();
    },

    determineTarget() {
      if (!isUndefined(this.target)) {
        return this.target;
      }

      if (this.isPhone()) {
        return '_self';
      }

      if (this.isExternal()) {
        return '_blank';
      }

      return '_self';
    },

    goToUrl(e) {
      if (this.isPhone()) {
        e.preventDefault();
      }

      this.trackAnalytics();

      if (this.isPhone()) {
        window.open(this.url);
        return;
      }

      if (!this.$inertia) {
        return;
      }

      if (isUndefined(this.url)) {
        return;
      }

      if (get(this, 'fullRedirect')) {
        return;
      }

      if (this.isExternal() || this.isPhone()) {
        return;
      }

      if (this.isNotInertiaCompatible()) {
        return;
      }

      e.preventDefault();
      this.$inertia.visit(this.url);
    },

    trackAnalytics() {
      if (this.isFacebookShare()) {
        Vue.analytics.trackBlogEvent(
          'Shared Post',
          ['mixpanel'],
          { ...this.analyticsData, Service: 'Facebook' },
        );
        return;
      }

      if (this.isTwitterShare()) {
        Vue.analytics.trackBlogEvent(
          'Shared Post',
          ['mixpanel'],
          { ...this.analyticsData, Service: 'Twitter' },
        );
        return;
      }

      if (this.isLinkedInShare()) {
        Vue.analytics.trackBlogEvent(
          'Shared Post',
          ['mixpanel'],
          { ...this.analyticsData, Service: 'LinkedIn' },
        );
        return;
      }

      if (this.isFacebookGroup()) {
        Vue.analytics.trackWebsiteEvent(
          'Visited Facebook Group',
          ['mixpanel', 'ga', 'fb'],
          {},
          'ViewContent',
        );
        return;
      }

      if (this.isInstagram()) {
        Vue.analytics.trackWebsiteEvent(
          'Visited Instagram Page',
          ['mixpanel', 'ga', 'fb'],
          {},
          'ViewContent',
        );
        return;
      }

      if (this.isPhone()) {
        Vue.analytics.trackWebsiteEvent(
          'Dialed Phone Number',
          ['mixpanel', 'ga', 'fb'],
          {},
          'Contact',
        );
        return;
      }

      if (this.isInfoEmail()) {
        Vue.analytics.trackWebsiteEvent(
          'Initiated Email to info@',
          ['mixpanel', 'ga', 'fb'],
          {},
          'Contact',
        );
        return;
      }

      if (this.isPhotoEmail()) {
        Vue.analytics.trackWebsiteEvent(
          'Initiated Email to photo@',
          ['mixpanel', 'ga', 'fb'],
          {},
          'Contact',
        );
      }
    },
  },
};
</script>

<style scoped>
    .icon-top {
        left: 50%;
    }
</style>
